import { useState, useMemo, useCallback } from "react";
import { CaretUp, CaretDown, CaretUpDown } from "@phosphor-icons/react";

/**
 * Hook for sortable table data.
 * Usage:
 *   const { sorted, sortKey, sortDir, onSort } = useSortable(data, "name", "asc");
 *   <SortTh sortKey="name" label="Name" current={sortKey} dir={sortDir} onSort={onSort} />
 */
export function useSortable(data, defaultKey = "", defaultDir = "asc") {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const onSort = useCallback((key) => {
    setSortKey(prev => {
      if (prev === key) {
        setSortDir(d => d === "asc" ? "desc" : "asc");
        return key;
      }
      setSortDir("asc");
      return key;
    });
  }, []);

  const sorted = useMemo(() => {
    if (!sortKey || !data?.length) return data || [];
    return [...data].sort((a, b) => {
      let va = a[sortKey];
      let vb = b[sortKey];
      // Handle nulls
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      // Numbers
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      // Booleans
      if (typeof va === "boolean") {
        return sortDir === "asc" ? (va === vb ? 0 : va ? -1 : 1) : (va === vb ? 0 : va ? 1 : -1);
      }
      // Strings
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      const cmp = sa.localeCompare(sb);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, onSort };
}

/**
 * Sortable table header cell.
 */
export function SortTh({ sortKey, label, current, dir, onSort, align = "left", className = "" }) {
  const isActive = current === sortKey;
  return (
    <th
      className={`${align === "right" ? "text-right" : "text-left"} px-4 py-3 text-xs font-bold text-[#525252] uppercase tracking-wider cursor-pointer select-none hover:text-[#0055FF] transition-colors ${isActive ? "text-[#0055FF]" : ""} ${className}`}
      onClick={() => onSort(sortKey)}
      data-testid={`sort-${sortKey}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (
          dir === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />
        ) : (
          <CaretUpDown size={12} className="opacity-30" />
        )}
      </span>
    </th>
  );
}
